import type { ComplianceItem, ComplianceState, Eligibility, JourneyStop, LatLng, LicenceCategory, VehicleType } from './types';

export const VEHICLE_TYPES: { key: VehicleType; label: string; licence: LicenceCategory; cpc: boolean; tacho: boolean }[] = [
  { key: 'car', label: 'Car', licence: 'B', cpc: false, tacho: false },
  { key: 'van', label: 'Van (up to 3.5t)', licence: 'B', cpc: false, tacho: false },
  { key: 'luton', label: 'Luton / box van (3.5t)', licence: 'B', cpc: false, tacho: false },
  { key: 'truck_7_5t', label: '7.5t truck', licence: 'C1', cpc: true, tacho: true },
  { key: 'hgv', label: 'HGV', licence: 'C', cpc: true, tacho: true },
];
export const LICENCE_CATEGORIES: LicenceCategory[] = ['B', 'BE', 'C1', 'C1E', 'C', 'CE'];
/** Holding a category also covers these (UK licence rules, simplified: C covers C1, CE covers everything). */
const COVERS: Record<LicenceCategory, LicenceCategory[]> = {
  B: ['B'], BE: ['B', 'BE'], C1: ['C1'], C1E: ['C1', 'C1E', 'BE'], C: ['C', 'C1'], CE: ['C', 'C1', 'CE', 'C1E', 'BE'],
};
export const coversCategory = (held: readonly LicenceCategory[], need: LicenceCategory) => held.some(h => h === need || COVERS[h]?.includes(need));

export const COMPLIANCE_ITEMS: { key: ComplianceItem; label: string }[] = [
  { key: 'mot', label: 'MOT' }, { key: 'tax', label: 'Road tax' }, { key: 'insurance', label: 'Insurance' }, { key: 'service', label: 'Service' }, { key: 'tacho', label: 'Tachograph calibration' },
];

const DAY = 86_400_000;
export const daysBetween = (fromIso: string, toIso: string) => Math.round((Date.parse(`${toIso.slice(0, 10)}T00:00:00Z`) - Date.parse(`${fromIso.slice(0, 10)}T00:00:00Z`)) / DAY);

/** Due on the day = still valid that day. */
export function complianceState(due: string | null, today: string, warnDays = 30, required = true): ComplianceState {
  if (!required) return 'not_required';
  if (!due) return 'not_set';
  const d = daysBetween(today, due);
  return d < 0 ? 'overdue' : d <= warnDays ? 'due_soon' : 'ok';
}

export interface EligibilityDriver { is_external: boolean; active: boolean; licence_categories: LicenceCategory[]; licence_expires_on: string | null; licence_last_checked_on: string | null; licence_check_every_months: number; cpc_expires_on: string | null; tacho_card_expires_on: string | null; points: number }
export interface EligibilityVehicle { required_licence: LicenceCategory; requires_cpc: boolean; vehicle_type: VehicleType; required_skill_id: string | null }

/**
 * Can this driver drive this vehicle on `onDate`? Hard reasons block assignment; warnings don't.
 * Skills: the vehicle may require a workforce skill (e.g. tail-lift trained) that must be valid on the day.
 */
export function driverEligibility(d: EligibilityDriver | null, v: EligibilityVehicle, onDate: string, skills: { skill_id: string; expires_on: string | null }[] = [], drivingMinutesThatDay = 0, jobMinutes = 0): Eligibility {
  const reasons: string[] = []; const warnings: string[] = [];
  if (!d) return { ok: false, reasons: ['No driver record (licence details missing)'], warnings };
  if (!d.active) reasons.push('Driver is inactive');
  if (!coversCategory(d.licence_categories, v.required_licence)) reasons.push(`Licence doesn't cover category ${v.required_licence}`);
  if (!d.licence_expires_on) reasons.push('Licence expiry not recorded');
  else if (daysBetween(onDate, d.licence_expires_on) < 0) reasons.push('Licence expired');
  if (!d.licence_last_checked_on) reasons.push('Licence never checked');
  else {
    const next = addMonths(d.licence_last_checked_on, d.licence_check_every_months);
    if (daysBetween(onDate, next) < 0) reasons.push(`Licence check overdue (was due ${next})`);
    else if (daysBetween(onDate, next) <= 14) warnings.push(`Licence check due ${next}`);
  }
  if (v.requires_cpc) {
    if (!d.cpc_expires_on) reasons.push('Driver CPC needed for this vehicle');
    else if (daysBetween(onDate, d.cpc_expires_on) < 0) reasons.push('Driver CPC expired');
  }
  if (VEHICLE_TYPES.find(t => t.key === v.vehicle_type)?.tacho) {
    if (!d.tacho_card_expires_on || daysBetween(onDate, d.tacho_card_expires_on) < 0) reasons.push('Valid tachograph card needed');
  }
  if (v.required_skill_id) {
    const s = skills.find(x => x.skill_id === v.required_skill_id);
    if (!s) reasons.push('Missing a skill this vehicle requires');
    else if (s.expires_on && daysBetween(onDate, s.expires_on) < 0) reasons.push('Required skill has expired');
  }
  if (d.points >= 9) warnings.push(`${d.points} penalty points`);
  const total = drivingMinutesThatDay + jobMinutes;
  if (total > 10 * 60) reasons.push(`Over the 10h daily driving limit (${(total / 60).toFixed(1)}h)`);
  else if (total > 9 * 60) warnings.push(`${(total / 60).toFixed(1)}h driving that day (9h normal limit)`);
  return { ok: reasons.length === 0, reasons, warnings };
}

export function addMonths(iso: string, months: number): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1 + months, 1));
  const last = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate();
  return new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), Math.min(d, last))).toISOString().slice(0, 10);
}

/** Great-circle distance in metres. */
export function haversineM(a: LatLng, b: LatLng): number {
  const R = 6_371_000, r = (x: number) => (x * Math.PI) / 180;
  const dLat = r(b.lat - a.lat), dLng = r(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Stop order minimising distance: nearest neighbour then 2-opt (used by the mock provider; Google optimises server-side). */
export function optimiseOrder(origin: LatLng, stops: LatLng[], returnToOrigin = true): number[] {
  const n = stops.length;
  if (n < 3) return stops.map((_, i) => i);
  const pts = [origin, ...stops];
  const d = (i: number, j: number) => haversineM(pts[i], pts[j]);
  const left = new Set(stops.map((_, i) => i + 1)); const tour: number[] = [];
  let cur = 0;
  while (left.size) { let best = -1, bd = Infinity; for (const j of left) { const x = d(cur, j); if (x < bd) { bd = x; best = j; } } tour.push(best); left.delete(best); cur = best; }
  const len = (t: number[]) => t.reduce((s, x, i) => s + d(i ? t[i - 1] : 0, x), 0) + (returnToOrigin ? d(t[t.length - 1], 0) : 0);
  let improved = true;
  while (improved) {
    improved = false;
    for (let i = 0; i < n - 1; i++) for (let k = i + 1; k < n; k++) {
      const t = [...tour.slice(0, i), ...tour.slice(i, k + 1).reverse(), ...tour.slice(k + 1)];
      if (len(t) + 1e-6 < len(tour)) { tour.splice(0, n, ...t); improved = true; }
    }
  }
  return tour.map(x => x - 1);
}

/** ETAs along legs from a start time, waiting for a window to open and adding a service time per stop. */
export function scheduleStops(start: string, legs: { duration_s: number }[], windows: { window_start: string | null }[], serviceMin = 15): { eta: string; depart: string }[] {
  let t = Date.parse(start); const out: { eta: string; depart: string }[] = [];
  legs.forEach((l, i) => {
    t += l.duration_s * 1000;
    const eta = t; const open = windows[i]?.window_start ? Date.parse(windows[i].window_start!) : 0;
    t = Math.max(t, open) + serviceMin * 60_000;
    out.push({ eta: new Date(eta).toISOString(), depart: new Date(t).toISOString() });
  });
  return out;
}

/** Late when the ETA (or arrival) is more than `graceMin` after the window end. */
export const isLate = (s: Pick<JourneyStop, 'window_end' | 'eta' | 'arrived_at'>, graceMin = 10) =>
  Boolean(s.window_end && (s.arrived_at ?? s.eta) && Date.parse((s.arrived_at ?? s.eta)!) > Date.parse(s.window_end) + graceMin * 60_000);

/** kg CO2e per litre (DEFRA 2024, average biofuel blend) and per kWh (UK grid). */
export const CO2_PER_LITRE: Record<string, number> = { diesel: 2.51, petrol: 2.08, hybrid: 2.08 };
export const CO2_PER_KWH = 0.207;
export const costPerMile = (costPence: number, miles: number) => (miles > 0 ? Math.round(costPence / miles) : null);
export const normaliseReg = (reg: string) => reg.toUpperCase().replace(/[^A-Z0-9]/g, '');
export const formatReg = (reg: string) => { const r = normaliseReg(reg); return /^[A-Z]{2}\d{2}[A-Z]{3}$/.test(r) ? `${r.slice(0, 4)} ${r.slice(4)}` : r; };
export const pct = (n: number, d: number) => (d ? Math.round((n / d) * 1000) / 10 : null);

/** Walkaround checklist (DVSA daily walkaround, condensed). */
export const WALKAROUND_ITEMS: { key: string; label: string }[] = [
  { key: 'lights', label: 'Lights & indicators' }, { key: 'tyres', label: 'Tyres & wheel nuts' }, { key: 'brakes', label: 'Brakes & air/hydraulics' },
  { key: 'mirrors', label: 'Mirrors, glass & wipers' }, { key: 'fluids', label: 'Oil, coolant, fuel/AdBlue leaks' }, { key: 'body', label: 'Bodywork & doors' },
  { key: 'load', label: 'Load security & tail-lift' }, { key: 'horn', label: 'Horn, seatbelts & warning lamps' },
];
